import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, Inject, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";

import { CatAreaIA, Matriz, MatrizAviso } from "../../shared/model/iso42001/matriz-riesgo.model";
import { MatrizRiesgoService } from "../matriz-riesgo.service";

export interface EditarMatrizData {
  matriz: Matriz;
}

/**
 * Editar los datos de una matriz: nombre, área, alcance, proceso y si
 * trabaja con activos.
 *
 * 29/09/2026, pedido de Norman: en el tablero de Gestión de Riesgos las
 * tarjetas ya no llevan «Abrir» ni «Editar» (se abren con un clic, como las
 * de «Nueva solicitud»), y lo que editaba el botón se edita desde ADENTRO de
 * la matriz, con el botón Editar de su encabezado.
 *
 * La clave del folio se enseña pero no se cambia: los riesgos ya emitidos la
 * traen en su consecutivo (MatrizBuss.actualizar la conserva igual). Y apagar
 * «trabaja con activos» con activos capturados lo rechaza el servidor.
 *
 * Avisos de monitoreo (29/09/2026): aquí se elige quién recibe los avisos
 * de ESTA matriz, porque cada área tiene su gente. Se guardan aparte, en
 * cuanto se guardan los datos de la matriz, y solo si cambiaron.
 *
 * Se cierra con la matriz guardada, o sin nada si se cancela.
 */
@Component({
  selector: "app-editar-matriz-ia",
  standalone: true,
  templateUrl: "./editar-matriz-ia.component.html",
  styleUrls: ["./editar-matriz-ia.component.css"],
  imports: [CommonModule, FormsModule, MatFormFieldModule, MatInputModule],
})
export class EditarMatrizIAComponent implements OnInit {
  /** Copia: cancelar no debe dejar a medias el encabezado de la matriz. */
  borrador: Matriz;
  areas: CatAreaIA[] = [];
  guardando = false;
  error = "";

  /** Cómo estaba al abrir: el aviso de «trabaja con activos» depende de eso. */
  private readonly conActivosAntes: boolean;

  readonly OPCIONES_ACTIVOS = [
    { valor: "NO", titulo: "No", texto: "Solo General y Plan de Gestión, lo normal en las áreas." },
    { valor: "SI", titulo: "Sí", texto: "Agrega la pestaña de Activos y el expediente por activo." },
  ];

  constructor(
    @Inject(MAT_DIALOG_DATA) data: EditarMatrizData,
    private ref: MatDialogRef<EditarMatrizIAComponent, Matriz | undefined>,
    private service: MatrizRiesgoService,
    private cdr: ChangeDetectorRef,
  ) {
    this.borrador = { ...data.matriz, aplicaActivos: data.matriz.aplicaActivos === "SI" ? "SI" : "NO" };
    this.conActivosAntes = this.borrador.aplicaActivos === "SI";
  }

  ngOnInit(): void {
    // El catálogo de áreas viaja con el listado del tablero; la lectura de
    // una sola matriz no lo trae. Del mismo listado sale cuántos activos
    // tiene (el mismo conteo con el que el servidor rechaza apagarlos).
    this.service.consultarMatrices().subscribe({
      next: (res) => {
        this.areas = (res && res.areas) || [];
        const esta = ((res && res.matrices) || []).find((m) => m.idMatriz === this.borrador.idMatriz);
        this.totalActivos = esta ? esta.totalActivos ?? 0 : null;
        this.cdr.detectChanges();
      },
      error: () => undefined,
    });
    this.cargarAvisos();
  }

  /* ---------- Avisos de monitoreo ---------- */

  /** Quiénes pueden recibirlos: los que abren el tablero de Gestión de Riesgos. */
  candidatos: MatrizAviso[] = [];
  /** Los marcados. */
  avisados = new Set<number>();
  cargandoAvisos = true;
  avisosError = "";
  /** Cómo estaba la lista al abrir: si no cambia, no se vuelve a guardar. */
  private avisadosAntes = "";

  private cargarAvisos(): void {
    this.service.consultarAvisos(this.borrador.idMatriz!).subscribe({
      next: (res) => {
        this.cargandoAvisos = false;
        if (!res || res.codigoError) {
          this.avisosError = (res && res.mensajeError) || "No se pudo consultar quién recibe los avisos.";
        } else {
          this.candidatos = res.candidatos || [];
          this.avisados = new Set((res.avisados || []).map((a) => a.idUsuario));
          this.avisadosAntes = this.clave(this.avisados);
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.cargandoAvisos = false;
        this.avisosError = "No se pudo consultar quién recibe los avisos.";
        this.cdr.detectChanges();
      },
    });
  }

  alternarAviso(idUsuario: number): void {
    if (this.avisados.has(idUsuario)) {
      this.avisados.delete(idUsuario);
    } else {
      this.avisados.add(idUsuario);
    }
  }

  private clave(ids: Set<number>): string {
    return Array.from(ids).sort((a, b) => a - b).join(",");
  }

  /**
   * Cuántos activos tiene capturados. null mientras no se sabe: en ese rato,
   * si trabajaba con activos, el selector se queda bloqueado por si acaso.
   */
  totalActivos: number | null = null;

  /**
   * Con activos capturados ya no se cambia (29/09/2026, pedido de Norman):
   * apagarlo dejaría esos activos sin pantalla donde verlos. El servidor lo
   * rechaza igual (MatrizBuss.actualizar); aquí ni se deja intentar.
   */
  get activosBloqueados(): boolean {
    return this.conActivosAntes && (this.totalActivos === null || this.totalActivos > 0);
  }

  /** Apagarlo sin activos capturados: se dice que no se pierde nada. */
  get apagaSinActivos(): boolean {
    return this.conActivosAntes && this.totalActivos === 0 && this.borrador.aplicaActivos !== "SI";
  }

  elegirActivos(valor: string): void {
    if (!this.activosBloqueados) {
      this.borrador.aplicaActivos = valor;
    }
  }

  cancelar(): void {
    this.ref.close();
  }

  guardar(): void {
    if (this.guardando) {
      return;
    }
    const nombre = (this.borrador.nombre || "").trim();
    if (!nombre) {
      this.error = "El nombre de la matriz es obligatorio.";
      return;
    }
    const cambios: Matriz = {
      ...this.borrador,
      nombre,
      alcance: (this.borrador.alcance || "").trim(),
      proceso: (this.borrador.proceso || "").trim(),
      // Bloqueado se manda como estaba, pase lo que pase en pantalla.
      aplicaActivos: this.activosBloqueados ? "SI" : this.borrador.aplicaActivos,
    };
    this.error = "";
    this.guardando = true;
    this.service.actualizarMatriz(cambios).subscribe({
      next: (res) => {
        this.guardando = false;
        // Sin cuerpo también es fallo: el manejador global responde 200 vacío.
        if (!res || res.codigoError) {
          this.error = (res && res.mensajeError) || "No se pudo guardar la matriz.";
          this.cdr.detectChanges();
          return;
        }
        // La lista de avisos va aparte, y solo si cambió o si no se pudo
        // leer (entonces no se toca).
        if (this.avisosError || this.clave(this.avisados) === this.avisadosAntes) {
          this.ref.close(cambios);
          return;
        }
        this.guardarAvisos(cambios);
      },
      error: () => {
        this.guardando = false;
        this.error = "No se pudo guardar la matriz. Vuelve a intentarlo.";
        this.cdr.detectChanges();
      },
    });
  }

  private guardarAvisos(cambios: Matriz): void {
    this.guardando = true;
    this.service.guardarAvisos(this.borrador.idMatriz!, Array.from(this.avisados)).subscribe({
      next: (res) => {
        this.guardando = false;
        if (!res || res.codigoError) {
          this.error = "Se guardaron los datos de la matriz, pero no la lista de avisos: "
            + ((res && res.mensajeError) || "vuelve a intentarlo.");
          this.cdr.detectChanges();
          return;
        }
        this.ref.close(cambios);
      },
      error: () => {
        this.guardando = false;
        this.error = "Se guardaron los datos de la matriz, pero no la lista de avisos. Vuelve a intentarlo.";
        this.cdr.detectChanges();
      },
    });
  }
}
